import React, {useState} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import {
    ORDER_STATUS,
    ORDER_STATUS_CONFIG,
    normalizeOrderStatus,
    formatOrderDate,
    safeField,
} from "../book/bookOrderAdminUtils";

const InfoRow = ({label, value}) => (
    <Box sx={{display: "flex", flexDirection: {xs: "column", sm: "row"}, py: 0.75}}>
        <Typography variant="body2" sx={{fontWeight: 600, width: {xs: "100%", sm: "160px"}, color: "text.secondary"}}>
            {label}:
        </Typography>
        <Typography variant="body2" sx={{color: "text.primary", wordBreak: "break-word", flex: 1}}>
            {safeField(value)}
        </Typography>
    </Box>
);

export const BookOrderDetailDialog = ({open, onClose, order, onStatusChange}) => {
    const [updating, setUpdating] = useState(false);

    if (!order) return null;

    const currentStatus = normalizeOrderStatus(order.status);
    const statusConfig = ORDER_STATUS_CONFIG[currentStatus] || ORDER_STATUS_CONFIG[ORDER_STATUS.NEW];

    const handleStatusSelect = async (e) => {
        const nextStatus = e.target.value;
        if (nextStatus === currentStatus || typeof onStatusChange !== "function") return;

        setUpdating(true);
        try {
            await onStatusChange(order.id, nextStatus);
        } finally {
            setUpdating(false);
        }
    };

    const deliveryAddressLine = [order.delivery?.areaCode, order.delivery?.place]
        .filter(Boolean)
        .join(" ")
        .trim();

    const invoiceAddressLine = [order.invoice?.areaCode, order.invoice?.place]
        .filter(Boolean)
        .join(" ")
        .trim();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="book-order-detail-title"
        >
            <DialogTitle
                id="book-order-detail-title"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                    pb: 2,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap"}}>
                    <Typography variant="h6" component="span" sx={{fontWeight: 700}}>
                        Bestilling #{order.id}
                    </Typography>
                    <Chip label={statusConfig.label} color={statusConfig.color} size="small" />
                </Box>
                <IconButton onClick={onClose} aria-label="Lukk">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{pt: 3}}>
                <Box sx={{display: "flex", flexDirection: "column", gap: 2.5}}>
                    {/* Status change control */}
                    <Box sx={{display: "flex", alignItems: "center", gap: 2, pt: 1}}>
                        <FormControl size="small" sx={{minWidth: 200}}>
                            <InputLabel id="order-status-label">Status</InputLabel>
                            <Select
                                labelId="order-status-label"
                                id="order-status-select"
                                value={currentStatus}
                                label="Status"
                                onChange={handleStatusSelect}
                                disabled={updating}
                            >
                                <MenuItem value={ORDER_STATUS.NEW}>Ny</MenuItem>
                                <MenuItem value={ORDER_STATUS.SENT}>Sendt</MenuItem>
                                <MenuItem value={ORDER_STATUS.INVOICED}>Fakturert</MenuItem>
                                <MenuItem value={ORDER_STATUS.CANCELLED}>Kansellert</MenuItem>
                            </Select>
                        </FormControl>
                        {updating && <CircularProgress size={20} />}
                    </Box>

                    <Divider />

                    {/* Order summary */}
                    <Box>
                        <Typography variant="subtitle2" component="h2" sx={{fontWeight: 700, mb: 1, color: "primary.main"}}>
                            Bestillingsdetaljer
                        </Typography>
                        <InfoRow label="Dato" value={formatOrderDate(order.createdAt)} />
                        <InfoRow label="Bestilt av" value={order.name} />
                        <InfoRow label="E-post" value={order.email} />
                        <InfoRow label="Telefon" value={order.phone} />
                        <InfoRow label="Antall bøker" value={order.numberOfBooks ? `${order.numberOfBooks} stk` : "—"} />
                    </Box>

                    <Divider />

                    {/* Delivery address */}
                    <Box>
                        <Typography variant="subtitle2" component="h2" sx={{fontWeight: 700, mb: 1, color: "primary.main"}}>
                            Leveringsadresse
                        </Typography>
                        <InfoRow label="Mottaker/virksomhet" value={order.delivery?.name} />
                        <InfoRow label="Gateadresse" value={order.delivery?.street} />
                        <InfoRow label="Poststed" value={deliveryAddressLine} />
                        <InfoRow label="Kontaktperson" value={order.delivery?.contactPerson} />
                        <InfoRow label="Telefon kontaktperson" value={order.delivery?.contactPhone} />
                    </Box>

                    <Divider />

                    {/* Invoice address */}
                    <Box>
                        <Typography variant="subtitle2" component="h2" sx={{fontWeight: 700, mb: 1, color: "primary.main"}}>
                            Fakturaopplysninger
                        </Typography>
                        <InfoRow label="Fakturanavn" value={order.invoice?.name} />
                        <InfoRow label="Organisasjonsnummer" value={order.invoice?.orgNumber} />
                        <InfoRow label="Referanse / PO" value={order.invoice?.reference} />
                        <InfoRow label="Fakturaadresse" value={order.invoice?.address} />
                        <InfoRow label="Poststed" value={invoiceAddressLine} />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{px: 3, pb: 2.5}}>
                <Button onClick={onClose} variant="outlined" color="primary">
                    Lukk
                </Button>
            </DialogActions>
        </Dialog>
    );
};
